/* global G, PLATFORM */
import map from 'lib/sequence/model/api/map'
import { asyncPipeSpread, asyncPipeSpreadIf, bulk } from 'lib/util'
import { get } from 'lib/sequence/component/state/value'
import { hidden, hide, show } from 'lib/sequence/component/state/hidden'
import find from 'lib/sequence/component/children/find'
import { set as setWidth } from 'lib/sequence/component/state/width'

const toggle = field => (!get(field) ? hide(field) : show(field))
const bulkToggle = bulk(toggle)

/**
 * Maps the incoming data to their respective ui elements.
 *
 * @param {Gaia.Component.Spec} module the current module composition object
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapProperties = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { description } = model[G.CHILDREN]
  const { deviceMalfunction } = description[G.CHILDREN]

  const {
    issue,
    deviceInfo,
    internal,
    deviceMalfunction: deviceMalfunctionField,
  } = components

  const bulkMapModel = bulk(map(model))

  bulkMapModel(issue, internal)
  map(description)(deviceInfo)

  deviceMalfunctionField[G.STATE].checked = deviceMalfunction[G.CACHE]

  return [components, ...args]
}

/**
 * Displays the deviceInfo fields according to their visibility.
 *
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayDeviceFields = () => async (components, ...args) => {
  const {
    deviceInfo,
    deviceMalfunction,
    deviceFirmwareVersion,
    pcSoftwareProgram,
    pcSoftwareVersion,
  } = components

  bulkToggle(
    deviceMalfunction,
    deviceFirmwareVersion,
    pcSoftwareProgram,
    pcSoftwareVersion,
  )

  !get(deviceMalfunction)
  && !get(deviceFirmwareVersion)
  && !get(pcSoftwareVersion)
  && !get(pcSoftwareProgram)
  && hide(deviceInfo)

  return [components, ...args]
}

/**
 * Displays the internal fields according to their visibility.
 *
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayInternalFields = () => async (components, ...args) => {
  const {
    internal,
    costObject,
    isCovered,
    chargeable,
  } = components

  bulkToggle(costObject)
  get(isCovered) === null && hide(isCovered)
  get(chargeable) === null && hide(chargeable)

  hidden(costObject)
    && hidden(isCovered)
    && hidden(chargeable)
    && hide(internal)

  return PLATFORM === 'web'
    ? [components, ...args]
    : args
}

/**
 * Arranges the sections according to their visibility.
 *
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const arrangeSections = () => async (components, ...args) => {
  const {
    internal,
    information,
    deviceInfo,
    description,
  } = components

  if (!hidden(deviceInfo) || !hidden(internal)) {
    setWidth(information, 4)
    setWidth(description, 8)
  }

  return args
}

/**
 * Ticket Description Action
 *
 * @param {Gaia.Component.Spec} module the current module composition object
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  mapProperties(module),
  displayDeviceFields(),
  displayInternalFields(),
  asyncPipeSpreadIf(() => PLATFORM === 'web')(
    arrangeSections(),
  ),
)(find(component), ...args)
